import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const fallbackRouteName = (pathname: string) => {
  const segment = pathname.split('/').filter(Boolean).pop();
  if (!segment) return 'Home';
  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

/** Announces route changes and moves keyboard focus to the current page landmark. */
const RouteAnnouncer = () => {
  const { pathname, hash } = useLocation();
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    let active = true;
    const timeout = window.setTimeout(() => {
      if (!active) return;

      const title = document.title.trim() || fallbackRouteName(pathname);
      setAnnouncement(`${title} loaded`);

      // ScrollRestoration owns hash targets. Do not steal focus back to the
      // main landmark after an anchored navigation has restored its target.
      if (!hash) {
        const main = document.querySelector('main');
        if (main instanceof HTMLElement) {
          if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
          main.focus({ preventScroll: true });
        }
      }
    }, 0);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [hash, pathname]);

  return (
    <div
      aria-atomic="true"
      aria-live="polite"
      className="sr-only"
      data-route-announcer
      role="status"
    >
      {announcement}
    </div>
  );
};

export default RouteAnnouncer;
